import React, { useState, useEffect } from 'react';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash, FaEye } from 'react-icons/fa';

const CountryPage = () => {
  const [countries, setCountries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [formValues, setFormValues] = useState({
    name: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);

  // Debounce search query - only trigger when 3+ characters or empty
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 3 || searchQuery.length === 0) {
        setDebouncedSearchQuery(searchQuery);
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchCountries(currentPage, debouncedSearchQuery);
  }, [currentPage, debouncedSearchQuery]);

  const fetchCountries = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const res = await axios.get(`/admin/api/country?page=${page}&limit=${limit}&search=${search}`);
      const { countries = [], totalCount = 0 } = res.data;
      setCountries(countries);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      console.error('Failed to fetch Countries:', err);
      toastError('Failed to fetch countries');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      name: { required: true },
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: formValues.name
      };

      if (editingId) {
        await axios.put(`/admin/api/country/${editingId}`, payload);
        toastSuccess('Country updated successfully!');
      } else {
        await axios.post('/admin/api/country', payload);
        toastSuccess('Country added successfully!');
      }
      setCurrentPage(1);
      fetchCountries(1, searchQuery);
      handleCancel();
    } catch (err) {
      console.error('Failed to save Country:', err);
      toastError(err.response?.data?.message || 'Failed to save Country');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (country) => {
    setFormValues({
      name: country.name || '',
    });
    setEditingId(country._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await confirmDelete('Are you sure you want to delete this country?');
    if (result?.isConfirmed) {
      try {
        await axios.delete(`/admin/api/country/${id}`);
        toastSuccess('Country deleted successfully!');
        setCurrentPage(1);
        fetchCountries(1, searchQuery);
      } catch (err) {
        console.error('Failed to delete Country:', err);
        toastError('Failed to delete Country');
      }
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({ name: '' });
    setEditingId(null);
    setErrors({});
  };

  const handleViewCountry = (country) => {
    setSelectedCountry(country);
    setShowViewModal(true);
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0"> Countries</h3>
        <input
          type="text"
          className="form-control w-25"
          placeholder="Search Countries (min 3 chars)..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
        />
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">Country List</h5>
          <button
            className="btn btn-sm btn-success"
            onClick={() => setShowModal(true)}
          >
            + Add New Country
          </button>
        </div>
       <div className="table-responsive">
        <div className="card-body p-0">
          <table
            className="table table-bordered table-sm mb-0"
          >
            <thead className="table-light">
              <tr>
                <th className="py-1 px-2 w-5">Sr no.</th>
                <th className="py-1 px-2">Name</th>
                <th className="py-1 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="3" className="text-center py-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : countries.length > 0 ? (
                countries.map((country, idx) => (
                  <tr key={country._id}>
                    <td className="py-1 px-2">{(currentPage - 1) * limit + idx + 1}</td>
                    <td className="py-1 px-2">{country.name}</td>
                    <td className="py-1 px-2">
                      <button
                        className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0 text-primary-custom"
                        title="Edit"
                        onClick={() => handleEdit(country)}
                      >
                        <FaEdit size={14} />
                      </button>
                      <button
                        className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0 text-danger"
                        title="Delete"
                        onClick={() => handleDelete(country._id)}
                      >
                        <FaTrash size={14} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="text-center py-2">No Countries found.</td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo;
              </button>

              {(() => {
                const pages = [];
                const maxVisiblePages = 3;
                
                // Always show first page
                pages.push(
                  <button
                    key={1}
                    className="{`btn btn-sm page-btn ${currentPage === 1 ? 'active' : ''}`}"
                    onClick={() => setCurrentPage(1)}
                  >
                    1
                  </button>
                );

                // Show ellipsis if needed before current page range
                if (currentPage > maxVisiblePages + 1) {
                  pages.push(
                    <span key="ellipsis1" className="px-2">
                      ...
                    </span>
                  );
                }

                // Show pages around current page
                const startPage = Math.max(2, currentPage - 1);
                const endPage = Math.min(totalPages - 1, currentPage + 1);

                for (let page = startPage; page <= endPage; page++) {
                  if (page !== 1 && page !== totalPages) {
                    pages.push(
                      <button
                        key={page}
                        className="{`btn btn-sm page-btn ${currentPage === page ? 'active' : ''}`}"
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    );
                  }
                }

                // Show ellipsis if needed after current page range
                if (currentPage < totalPages - maxVisiblePages) {
                  pages.push(
                    <span key="ellipsis2" className="px-2">
                      ...
                    </span>
                  );
                }

                // Always show last page if there's more than one page
                if (totalPages > 1) {
                  pages.push(
                    <button
                      key={totalPages}
                      className="{`btn btn-sm page-btn ${currentPage === totalPages ? 'active' : ''}`}"
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  );
                }

                return pages;
              })()}

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
      <CommonModalForm
        visible={showModal}
        title={editingId ? 'Edit Country' : 'Add Country'}
        fields={[
          { 
            name: 'name', 
            label: 'Country Name', 
            type: 'text',
            placeholder: 'Enter Country Name'
          },
        ]}
        values={formValues}
        errors={errors}
        onChange={setFormValues}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitLabel={saving ? (editingId ? 'Updating...' : 'Saving...') : (editingId ? 'Update' : 'Save')}
        requiredFields={['name']}
        disabled={saving}
      />

      {/* View Country Modal */}
      {showViewModal && selectedCountry && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Country Details</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowViewModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Name:</strong> {selectedCountry.name}</p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>Created:</strong> {new Date(selectedCountry.createdAt).toLocaleDateString()}</p>
                  </div>
                  {selectedCountry.updatedAt && (
                    <div className="col-md-6">
                      <p><strong>Updated:</strong> {new Date(selectedCountry.updatedAt).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryPage;
